import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getOtcSettlementProviders,
  createOtcSettlementProvider,
  CreateOtcSettlementProviderPayload,
} from "../services";
import { OtcSettlementProvider } from "../types";

export interface UseOtcProvidersOptions {
  currencyFrom?: string;
  currencyTo?: string;
  enabled?: boolean;
}

export const otcProviderKeys = {
  all: ["otc-providers"] as const,
  list: (currencyFrom: string, currencyTo: string) =>
    [...otcProviderKeys.all, currencyFrom, currencyTo] as const,
};

export interface UseOtcProvidersResult {
  providers: OtcSettlementProvider[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

export const useOtcProviders = ({
  currencyFrom = "USD",
  currencyTo = "NGN",
  enabled = true,
}: UseOtcProvidersOptions = {}): UseOtcProvidersResult => {
  const normalizedFrom = currencyFrom.toUpperCase().trim();
  const normalizedTo = currencyTo.toUpperCase().trim();

  const {
    data: providers = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<OtcSettlementProvider[]>({
    queryKey: otcProviderKeys.list(normalizedFrom, normalizedTo),
    queryFn: () => getOtcSettlementProviders(normalizedFrom, normalizedTo),
    enabled: Boolean(enabled && normalizedFrom && normalizedTo),
  });

  return {
    providers,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useCreateOtcProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateOtcSettlementProviderPayload) =>
      createOtcSettlementProvider(payload),
    onSuccess: (newProvider, variables) => {
      const from = (variables.currencyFrom || "USD").toUpperCase().trim();
      const to = (variables.currencyTo || "NGN").toUpperCase().trim();

      queryClient.setQueryData<OtcSettlementProvider[]>(
        otcProviderKeys.list(from, to),
        (old?: OtcSettlementProvider[]) => {
          const list = old || [];
          const exists = list.some(
            (p: OtcSettlementProvider) =>
              (p._id && p._id === newProvider._id) ||
              p.name.toLowerCase() === newProvider.name.toLowerCase()
          );
          if (exists) return list;
          return [...list, newProvider];
        }
      );

      queryClient.invalidateQueries({
        queryKey: otcProviderKeys.list(from, to),
      });
    },
  });
};
